"use client";

import { useTranslations } from "next-intl";

const h2 = "font-display text-xl font-bold text-text-primary";
const p = "mt-3 text-text-body text-sm leading-relaxed";

export function TermsPage() {
  const t = useTranslations("Terms");

  return (
    <section className="pt-8 lg:pt-32 pb-12 lg:pb-20 px-6">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-text-primary">
          {t("title")}
        </h1>
        <p className="mt-2 text-text-muted text-sm">{t("lastUpdated")}</p>

        <div className="mt-12 space-y-10">
          {/* Applicability */}
          <div>
            <h2 className={h2}>Applicability</h2>
            <p className={p}>
              These terms of use apply to the services provided by{" "}
              <strong className="text-text-primary">Scaffcalc AB</strong>,
              559229-1362 (hereinafter &ldquo;Scaffcalc&rdquo;,
              &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;), to you
              as a customer (hereinafter also &ldquo;you&rdquo; or
              &ldquo;Customer&rdquo;) unless otherwise agreed in writing.
            </p>
          </div>

          {/* 1. Service and Scope */}
          <div>
            <h2 className={h2}>1. Service and Scope</h2>
            <p className={p}>
              We provide the services (hereinafter &ldquo;
              <strong className="text-text-primary">the Service</strong>&rdquo;)
              as set forth herein and in the master agreement (hereinafter
              &ldquo;
              <strong className="text-text-primary">
                the Master Agreement
              </strong>
              &rdquo;), and any additional services added after the
              Agreement&apos;s commencement. The Service are governed by terms
              set out in the Master Agreement and these terms of use
              (collectively referred to as the &ldquo;
              <strong className="text-text-primary">Agreement</strong>&rdquo;).
              In the event of any conflict or inconsistency between these terms
              of use and the Master Agreement, the provisions of the Master
              Agreement shall prevail. In regard to consultancy services, as set
              out under Section 7.4 below, ABK09 shall take precedence over the
              Master Agreement.
            </p>
            <p className={p}>
              The Service includes access to a software and an application for
              easy scaffolding, including a calculation-tool in regard to
              requirements for quantity and strength (the &ldquo;
              <strong className="text-text-primary">Calculator</strong>&rdquo;).
              The functions and use of the Calculator depends on which plan the
              Customer has signed up for in the Master Agreement.
            </p>
            <p className={p}>
              The Service is provided by giving the Customer access to it
              through a user account on Scaffcalc&apos;s website. We provide the
              Customer with user instructions for using the Services. The
              Customer is then responsible for entering the requested
              information into the Calculator, after which the customer is
              provided with calculations and design documentation.
            </p>
            <p className={p}>
              Calculations and design documentation are provided based on the
              information entered by the Customer. The calculation by the
              Calculator does not take into account any information other than
              that provided to us by the Customer in this manner. It is the
              responsibility of the Customer to ensure that the information
              provided into the Calculator is correct, no essential information
              has been left out, and to ensure that the Service has adequate
              internet connection.
            </p>
            <p className={p}>
              In the event that circumstances change in relation to the original
              information entered, so that the information in the Calculator is
              no longer accurate, the Customer is responsible for making a new
              calculation. Scaffcalc is not responsible, against Customer or any
              third party, for the accuracy of the information provided by the
              Customer, for inaccurately entered information, or for relevant
              information omitted by the Customer. You are solely responsible
              for data and information entered and warrant that it does not
              infringe on any third party rights or violate any applicable laws.
              You will indemnify and hold us harmless from any claims arising
              due to breach of this undertaking.
            </p>
            <p className={p}>
              The calculations and design documentation performed/issued within
              the scope of the Service aim to comply with applicable parts of
              applicable Swedish standards for the Service and to meet the
              requirements set forth by applicable Swedish law or regulation,
              including the Swedish Work Environment Authority&apos;s
              regulations 2013:4.
            </p>
            <p className={p}>
              The scope of the Service is determined by the Master Agreement and
              these terms of use. If the documents conflict with each other, the
              Master Agreement shall prevail.
            </p>
          </div>

          {/* 2. Agreed Price */}
          <div>
            <h2 className={h2}>2. Agreed Price</h2>
            <p className={p}>
              The agreed price for the Service is as set forth in the Master
              Agreement. A budget can be established upon request for variable
              fees. If the parties have not agreed on a fixed price, the
              Customer shall pay the prices and applicable fees set out in
              Scaffcalc&apos;s price list available upon request, as applicable
              from time to time.
            </p>
            <p className={p}>
              Unless otherwise stated in the Master Agreement, compensation for
              calculations and design documentation for scaffolding is a fixed
              monthly fee, independent of the number of calculations made by the
              Customer. For calculations and design documentation for
              scaffolding with weather protection roofs/temporary roof systems,
              compensation is normally per calculation and design documentation.
            </p>
            <p className={p}>
              Scaffcalc has the right to change the price for the Service
              according to the price increase percentage below.
            </p>
            <p className={p}>
              As we continue to add more tools and introduce new product
              features, our plans and prices may change. We may also adjust
              plans and prices in response to local market changes, such as
              changes in local taxes or inflation. The yearly price increase is
              expected to follow the Swedish consumer price index (KPI) plus 4
              per cent but may be more or less. Scaffcalc reserves the right to
              adjust its pricing for services and products, both existing and
              new contracts, globally at its sole discretion. These changes may
              be implemented without prior notice to users, and it is the
              responsibility of the user to review the current pricing
              regularly. Renewal at the adjusted rate constitutes acceptance of
              the new price. Price adjustments will be in line with market
              standards and reasonable under the circumstances.
            </p>
          </div>

          {/* 3. Contract Term */}
          <div>
            <h2 className={h2}>3. Contract Term, Termination, etc.</h2>
            <p className={p}>
              The contract term is one (1) year and is effective from the time
              the Customer gains access to their account on the Scaffcalc
              website. Termination of the Agreement must be made no later than 1
              month before the contract term expires; otherwise, the Agreement
              is extended for another year, with a corresponding notice period.
            </p>
            <p className={p}>
              Each party has the right to terminate the Agreement for immediate
              termination if the other party has been declared bankrupt,
              initiated composition negotiations, is subject to corporate
              restructuring, or is otherwise insolvent. Moreover, Scaffcalc has
              the right to terminate the Agreement if you are in arrears with a
              payment for the Service with more than three (3) months.
            </p>
            <p className={p}>
              Upon termination of the Agreement, Scaffcalc shall be entitled to
              charge the Customer for the period during which the Service was
              available to the Customer.
            </p>
            <p className={p}>
              With regard to design documents for scaffolds with weather
              protection/temporary roof systems, the Agreement for this part of
              the Service shall terminate when the design documents have been
              provided to the Customer.
            </p>
          </div>

          {/* 4. Billing */}
          <div>
            <h2 className={h2}>4. Billing</h2>
            <p className={p}>
              Billing shall take place monthly on the last Friday of each month.
              If the last Friday of the month falls on a holiday, billing shall
              instead take place on the preceding business day. Billing may take
              place at a later time. Billing may also be done annually or
              quarterly if agreed upon and shall take place after the Agreement
              has been signed.
            </p>
            <p className={p}>
              Our invoices are normally due for payment 14 days after the
              invoice date and are issued to you, as the Customer. In the event
              of non-payment, interest on overdue payments shall be charged on
              the outstanding amount in accordance with applicable law from the
              invoice&apos;s due date until the payment is received.
            </p>
          </div>

          {/* 5. Acceptable Use */}
          <div>
            <h2 className={h2}>5. Acceptable Use</h2>
            <p className={p}>
              You may use the Service solely for internal business purposes
              within your organisation and for no other purpose, and in no other
              way, than what is set out in the Agreement.
            </p>
            <p className={p}>
              You agree not to use the Service for any illegal or unauthorized
              purpose. You shall comply with all applicable laws and
              regulations.
            </p>
          </div>

          {/* 6. Support */}
          <div>
            <h2 className={h2}>6. Support</h2>
            <p className={p}>
              Scaffcalc provides support on weekdays between 09:00-16:00 (CET
              during standard time and CEST during daylight saving time) for any
              errors or disruptions on the website, in the Calculator, or of any
              other kind. Scaffcalc undertakes to respond to inquiries within 3
              business days.
            </p>
            <p className={p}>
              We may from time to time update, change or modify the Service.
              Changes may for example be due to bug-fixes, new or improved
              functions or new modules. Those changes may require you to
              download and/or install updates or patches to the Service which
              will alter the Service. You hereby accept and understand that
              downloading and installing such changes may alter the
              Service&apos;s function and how you interact with the Service. If
              Scaffcalc make updates available to you, you shall install them
              for the proper functioning of the Service. If you use the Service
              without installing updates which have been made available to you,
              Scaffcalc cannot be held responsible for any malfunction in your
              use of the Service.
            </p>
            <p className={p}>
              Please note that Scaffcalc may solely determine if, when, and how
              and in what manner updates or upgrades are provided. Scaffcalc
              does not guarantee updates or upgrades not that any specific
              changes are made to the Service.
            </p>
          </div>

          {/* 7. Consultancy Services */}
          <div>
            <h2 className={h2}>7. Consultancy Services</h2>
            <p className={p}>
              If agreed between the parties, Scaffcalc may provide consultancy
              services in regard to the use of the Service. Such consultancy
              services shall be governed by the terms set out in Allm&auml;nna
              Best&auml;mmelser ABK 09 f&ouml;r konsultuppdrag inom arkitekt-
              och ingenj&ouml;rsverksamhet. The foregoing does however not apply
              in regard to section 4:6, 5:3, 7:10 and chapter 9&ndash;10 of
              ABK09, where the corresponding terms of these terms of use shall
              apply instead.
            </p>
          </div>

          {/* 8. Errors */}
          <div>
            <h2 className={h2}>8. Errors in the Service and Complaints</h2>
            <p className={p}>
              In the event of an error in the Service caused by Scaffcalc, the
              Customer shall immediately lodge a complaint by notifying us in
              writing of how the Customer considers the Service to be defective.
              The Customer must lodge a complaint no later than 5 days after the
              error has been detected or should have been detected; otherwise,
              the Customer loses their right to claim compensation for the
              error.
            </p>
            <p className={p}>
              Complaints due to the Customer believing that a calculation or
              design document is incorrect must be made no later than 3 months
              after the calculation was performed; otherwise, the Customer shall
              not have the right to claim compensation for the error.
            </p>
            <p className={p}>
              We are never liable for deficiencies in calculations, design
              documents, or other deficiencies, claims or losses resulting from
              any acts or omission from the Customer or from incorrect
              information provided by the Customer, or any errors caused by the
              Customer. We are not responsible for deficiencies resulting from
              the Customer providing information that differs from reality or
              where essential information has been left out.
            </p>
          </div>

          {/* 9. Consequences */}
          <div>
            <h2 className={h2}>9. Consequences of Errors and Liability</h2>
            <p className={p}>
              In the event of an error, Scaffcalc shall be given the opportunity
              to remedy the error. If the error concerns a calculation error,
              Scaffcalc shall be given the opportunity to redo the calculation.
              Only if the Customer has given Scaffcalc the opportunity to remedy
              the error and given notice of the error as set out under 8 above,
              and Scaffcalc determines that the error cannot be remedied, shall
              the Customer have the right to claim other consequences, such as
              price reductions or damages.
            </p>
            <p className={p}>
              The Customer is entitled to compensation only for damage caused by
              us through gross negligence. The Customer shall, as far as
              possible, take measures to limit the extent of the damage.
            </p>
            <p className={p}>
              Under no circumstances shall Scaffcalc be liable for indirect
              damage incurred by the Customer or in the Customer&apos;s business
              operations as a result of using the Service. Indirect damage
              refers to damage that does not have a direct connection to the
              damaging event, such as lost profits, production loss,
              interruptions in construction or decreased sales.
            </p>
            <p className={p}>
              Scaffcalc&apos;s total liability for damage during the term of the
              Agreement, including consultancy services, is limited, regardless
              of the cause and whether the damage results from errors in
              calculations and design documents for scaffolds or scaffolds with
              weather protection roofs/temporary roof systems, to an amount
              equivalent to two annual fees for the Service. The annual fee
              refers to the annual fee stated in the Master Agreement. If the
              insurance payout from Scaffcalc&apos;s insurance provider is less
              than two annual fees for the Service, however, Scaffcalc&apos;s
              liability is limited to the amount equivalent to the resulting
              insurance payout.
            </p>
            <p className={p}>
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as
              available&rdquo;. We are not liable for any loss incurred by you,
              unless caused by our breach of contract. The Service is provided
              without any warranties, whether expressed or implied. We do not
              warrant that Service will be error-free, uninterrupted or secure.
              Neither do we warrant or guarantee any fitness for a particular
              purpose or non-infringement.
            </p>
            <p className={p}>
              For the avoidance of doubt, other than accuracy of the
              calculations provided under Service Scaffcalc has no liability
              what so ever. That is, Scaffcalc is not liable for the actual
              assembly or quality of the scaffolding. It is the Customer&apos;s
              responsibility to ensure that all assembly based on the
              calculations provided by the Service is in accordance with the
              exact calculations and that the assembly otherwise is conducted in
              a safe manner and otherwise in compliance with best industry
              practice. The Customer undertakes to indemnify and keep Scaffcalc
              harmless from any claims arisen due to Customer&apos;s inaccurate
              assembly or otherwise non-compliance with best industry practice.
            </p>
          </div>

          {/* 10. IP */}
          <div>
            <h2 className={h2}>
              10. Information and Intellectual Property Rights
            </h2>
            <p className={p}>
              The Customer understands that the Service is dependent on data
              being collected from the Customer and analysed by Scaffcalc. The
              Customer grants Scaffcalc the right to use this data to improve
              the Service and develop new features, provided that the
              Customer&apos;s data is anonymized and cannot be traced back to
              the Customer or any individual.
            </p>
            <p className={p}>
              All intellectual property rights, including but not limited to
              copyright, trademarks, and patents, relating to the Service and
              any accompanying materials, including but not limited to
              calculations, design documents, software, and documentation,
              belong to Scaffcalc or its licensors. The Customer acquires no
              ownership rights to the Service or any accompanying materials but
              only a during the term of the Agreement limited, non-transferable,
              non-sublicensable and revocable non-exclusive right to use the
              Service solely for the purposes set out in the Agreement.
            </p>
            <p className={p}>
              Scaffcalc will develop and maintain the Service. Any such
              amendments, improvements and modification of the Service or other
              intellectual property rights are held by Scaffcalc, including any
              changes or modifications which are developed or created as a
              result of Agreement with, or suggestion from, you.
            </p>
            <p className={p}>
              The Customer is not permitted to copy, reproduce, distribute,
              sell, rent, lease, sublicense, or otherwise transfer the Service
              or any accompanying materials to third parties without the prior
              written consent of Scaffcalc.
            </p>
          </div>

          {/* 11. Confidentiality */}
          <div>
            <h2 className={h2}>11. Confidentiality</h2>
            <p className={p}>
              The parties undertake to treat all information about the other
              party&apos;s business operations, which is not generally known, as
              confidential. The duty of confidentiality applies both during the
              term of the Agreement and after its termination.
            </p>
            <p className={p}>
              The duty of confidentiality does not apply to information that a
              party can demonstrate was known to the party before it was
              received from the other party, or to information that the party
              has received or will receive from a third party without being
              bound by a duty of confidentiality concerning this information, or
              to information that is or will become publicly known other than
              through a breach of the duty of confidentiality by a party.
            </p>
            <p className={p}>
              You undertake to not share your login information to the Service
              with any third party or otherwise allow anyone else to use the
              Service or information provided therein.
            </p>
          </div>

          {/* 12. Personal Data */}
          <div>
            <h2 className={h2}>12. Personal Data</h2>
            <p className={p}>
              By signing up for and using the Service, you may be submitting
              personal data to Scaffcalc. All personal data is processed in
              accordance with Scaffcalc privacy policy, available on
              Scaffcalc&apos;s webpage.
            </p>
            <p className={p}>
              If you are a personal data controller and Scaffcalc will be
              processing your personal data on your behalf, you must enter a
              data processing agreement with us prior to us processing such
              personal data.
            </p>
          </div>

          {/* 13. Force Majeure */}
          <div>
            <h2 className={h2}>13. Force Majeure</h2>
            <p className={p}>
              Neither party shall be held responsible for any delay or failure
              in performance of its obligations under this Agreement if such
              delay or failure is due to circumstances beyond the party&apos;s
              reasonable control, including but not limited to war,
              insurrection, natural disasters, epidemic or pandemic, fire,
              strikes, lockouts, embargoes, governmental regulations, national
              or local power or internet outages, or any other similar
              circumstances. If a force majeure event continues for more than 30
              consecutive days, either party may terminate this Agreement by
              providing written notice to the other party without incurring any
              liability.
            </p>
          </div>

          {/* 14. Amendments */}
          <div>
            <h2 className={h2}>14. Amendments and Supplements</h2>
            <p className={p}>
              Scaffcalc reserves the right to make amendments and supplements to
              this Agreement. Such amendments and supplements shall be
              communicated to the Customer by email or through the Service. The
              Customer shall be deemed to have accepted the amendments and
              supplements if the Customer does not object to them in writing
              within 30 days from the date the amendments and supplements were
              communicated to the Customer. If the Customer does not accept the
              amendments and communicate this to Scaffcalc in accordance with
              the above, the Customer shall not be bound by such amendments but
              the Agreement shall be terminated with fourteen (14) days&apos;
              notice.
            </p>
          </div>

          {/* 15. Applicable Law */}
          <div>
            <h2 className={h2}>15. Applicable Law and Dispute Resolution</h2>
            <p className={p}>
              This Agreement shall be governed by the substantive law of Sweden.
            </p>
            <p className={p}>
              Any dispute, controversy or claim arising out of or in connection
              with this Agreement, or the breach, termination or invalidity
              thereof, shall be finally settled by arbitration administered by
              the Arbitration Institute of the Stockholm Chamber of Commerce
              (the &ldquo;<strong className="text-text-primary">SCC</strong>
              &rdquo;). The Rules for Expedited Arbitrations shall apply, unless
              the SCC in its discretion determines, taking into account the
              complexity of the case, the amount in dispute and other
              circumstances, that the Arbitration Rules shall apply. In the
              latter case, the SCC shall also decide whether the Arbitral
              Tribunal shall be composed of one or three arbitrators.
            </p>
            <p className={p}>
              The seat of arbitration shall be Stockholm, Sweden.
            </p>
            <p className={p}>
              The language to be used in the arbitral proceedings shall be
              English.
            </p>
            <p className={p}>
              The Parties undertake and agree that all arbitral proceedings
              conducted with reference to this arbitration clause will be kept
              strictly confidential. This confidentiality undertaking shall
              cover all information disclosed in the course of such arbitral
              proceedings, as well as any decision or award that is made or
              declared during the proceedings. Information covered by this
              confidentiality undertaking may not be disclosed to a third party
              without the prior consent by the other Party. Exceptions to the
              foregoing shall only apply to the extent that disclosure may be
              required of a Party due to mandatory law, an order of a competent
              court or public authority, or to protect, fulfil or pursue a
              legitimate legal right or obligation or to enforce or challenge an
              award.
            </p>
          </div>

          {/* 16. Miscellaneous */}
          <div>
            <h2 className={h2}>16. Miscellaneous</h2>
            <p className={p}>
              If any provision of this Agreement is found to be invalid or
              unenforceable, the remaining provisions shall continue in full
              force and effect. The parties agree to negotiate in good faith to
              replace any invalid or unenforceable provision with a valid and
              enforceable provision that achieves the original intent and
              economic effect of the invalid or unenforceable provision.
            </p>
            <p className={p}>
              This Agreement, together with any schedules, exhibits, or other
              attachments, constitutes the entire agreement between the parties
              regarding the subject matter hereof and supersedes any and all
              prior or contemporaneous agreements, understandings, and
              representations, whether written or oral, between the parties
              relating to the subject matter of this Agreement.
            </p>
          </div>

          {/* 17. Assignment */}
          <div>
            <h2 className={h2}>17. Assignment</h2>
            <p className={p}>
              The Customer may not assign, pledge or otherwise encumber this
              Agreement or any of its rights or obligations under this Agreement
              without the prior written consent of Scaffcalc. Scaffcalc is
              entitled to assign this Agreement to another corporate entity
              within the same company group without the prior consent of
              Customer.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
